import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, User, Calendar, Briefcase } from 'lucide-react';
import { searchAPI } from '../utils/api';

const EMPTY = { crew: [], events: [], projects: [] };

const Section = ({ title, children }) => (
  <div className="mb-1">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-3 pt-3 pb-1">{title}</p>
    {children}
  </div>
);

const Row = ({ icon: Icon, label, sub, onClick, testid }) => (
  <button onClick={onClick} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-secondary transition-colors text-left" data-testid={testid}>
    <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center shrink-0"><Icon size={16} /></div>
    <div className="min-w-0">
      <p className="text-sm font-medium truncate">{label}</p>
      {sub && <p className="text-xs text-muted-foreground truncate">{sub}</p>}
    </div>
  </button>
);

export const GlobalSearch = ({ open, onClose }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQ('');
      setResults(EMPTY);
    }
  }, [open]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(EMPTY); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchAPI.query(q.trim());
        setResults(res.data);
      } catch {
        setResults(EMPTY);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  if (!open) return null;

  const go = (path) => { onClose(); navigate(path); };
  const hasResults = results.crew.length || results.events.length || results.projects.length;
  const showHint = q.trim().length < 2;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 pt-[10vh]"
      onClick={onClose}
      data-testid="global-search-overlay"
    >
      <div className="w-full max-w-lg bg-card rounded-3xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="global-search-modal">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <Search size={18} className="text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search crew, events, projects…"
            className="flex-1 bg-transparent outline-none text-base"
            data-testid="global-search-input"
          />
          <button onClick={onClose} className="p-1 rounded-full hover:bg-secondary" data-testid="global-search-close"><X size={18} /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {showHint && <p className="text-sm text-muted-foreground text-center py-8">Type at least 2 characters to search…</p>}
          {!showHint && loading && <p className="text-sm text-muted-foreground text-center py-8">Searching…</p>}
          {!showHint && !loading && !hasResults && <p className="text-sm text-muted-foreground text-center py-8" data-testid="global-search-empty">No results for “{q}”.</p>}

          {results.crew.length > 0 && (
            <Section title="Crew">
              {results.crew.map(c => (
                <Row key={c.crew_id} icon={User} label={c.full_name} sub={c.role} onClick={() => go('/crew')} testid={`search-crew-${c.crew_id}`} />
              ))}
            </Section>
          )}
          {results.events.length > 0 && (
            <Section title="Events">
              {results.events.map(e => (
                <Row key={e.event_id} icon={Calendar} label={e.event_name || 'Event'} sub={e.event_date} onClick={() => go('/schedule')} testid={`search-event-${e.event_id}`} />
              ))}
            </Section>
          )}
          {results.projects.length > 0 && (
            <Section title="Projects">
              {results.projects.map(p => (
                <Row
                  key={p.project_id}
                  icon={Briefcase}
                  label={(p.bride_name && p.groom_name) ? `${p.bride_name} & ${p.groom_name}` : (p.project_name || p.name || p.project_id)}
                  sub={p.project_id}
                  onClick={() => go('/projects')}
                  testid={`search-project-${p.project_id}`}
                />
              ))}
            </Section>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
